import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const cadenceLegendDefs: Record<string, string> = {
  'Time in zone': 'Percentage of total riding time spent in each cadence range (RPM).',
};

interface CadenceZone {
  zone: string;
  min_rpm: number;
  max_rpm: number;
  percentage: number;
  hours: number;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const zoneColors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed'];

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{d?.label}</div>
      <div style={{ color: '#555' }}>{d?.percentage?.toFixed(1)}%</div>
      {d?.hours !== undefined && <div style={{ color: '#999' }}>{d.hours.toFixed(1)} hours</div>}
    </div>
  );
};

interface CadenceChartProps {
  targetUserId?: number | null;
}

const CadenceChart: React.FC<CadenceChartProps> = ({ targetUserId }) => {
  const [zones, setZones] = useState<CadenceZone[]>([]);
  const [dataPoints, setDataPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ChartRange>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/analytics/cadence?${params}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const json = await res.json();
        setZones(json.zones || []);
        setDataPoints(json.data_points || 0);
      } else {
        setZones([]);
      }
    } catch {
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, [range, targetUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = zones.map((z, i) => ({
    label: `${z.min_rpm}-${z.max_rpm}`,
    percentage: z.percentage,
    hours: z.hours,
    fill: zoneColors[i % zoneColors.length],
  }));

  return (
    <div className="analytics-card">
      <h3>Cadence Profile</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="analytics-chart-loading">Loading...</div>
      ) : zones.length === 0 ? (
        <div className="analytics-chart-empty">Not enough data for cadence analysis</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#999' }}
                label={{ value: 'RPM', position: 'insideBottomRight', offset: -5, fontSize: 10, fill: '#999' }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#999' }}
                label={{ value: '%', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#999' }}
              />
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                formatter={legendTooltipFormatter(cadenceLegendDefs)}
              />
              <Bar dataKey="percentage" name="Time in zone">
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="analytics-cadence-confidence">
            Based on {dataPoints.toLocaleString()} data points
          </div>
        </>
      )}
    </div>
  );
};

export default CadenceChart;
